import { MdOutlineInfo, MdPersonAddAlt1 } from "react-icons/md";
import { FILE_STORAGE_SERVER_URL } from "src/helpers/constants";
import { memo, useId, useMemo, useState } from "react";
import {
  getCountryDataByCode,
  getShortName,
  showNotification,
} from "src/helpers/utils";
import useMobile from "src/hooks/useMobile";
import useUsers from "src/hooks/useUsers";
import NetworkProfile from "../NetworkProfile/NetworkProfile";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import useCache from "src/hooks/useCache";
import { useTranslation } from "react-i18next";
import useInvites from "src/hooks/useInvites";
import { EPrivacyVisibility } from "../CurrentUserPrivacyModal/CurrentUserPrivacyModal";
import useCurrentUser from "src/hooks/useCurrentUser";
import ContactCard, { EContactCardType } from "./ContactCard";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface ISendContactInviteCard {
  user: string;
  animationIndex: number;
}

const SendContactInviteCard: React.FC<ISendContactInviteCard> = ({
  user,
  animationIndex,
}) => {
  const { isMobile } = useMobile();
  const { t } = useTranslation();
  const { clearCache } = useCache();
  const { getUserById } = useUsers();
  const { userInfo: currentUserInfo } = useCurrentUser();
  const tooltipId = useId();
  const { resetInvitesCache, sendContactInvite } = useInvites();
  const [showUserInfoModal, setShowUserInfoModal] = useState<boolean>(false);
  const [showSendDialog, setShowSendDialog] = useState<boolean>(false);

  const [isInviting, setIsInviting] = useState<boolean>(false);

  const userInfo = useMemo(() => {
    return getUserById({ _id: user });
  }, [user]);

  const privacySettings = useMemo(() => {
    return userInfo?.settings?.privacy || {};
  }, [userInfo]);

  const userImage = useMemo(() => {
    if (
      userInfo?.picture &&
      userInfo?.settings?.privacy?.seePicture === EPrivacyVisibility.EVERYONE
    ) {
      return `${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${userInfo?.picture}`;
    }

    return null;
  }, [userInfo]);

  const seeImage = useMemo(() => {
    return (
      privacySettings?.seePicture === EPrivacyVisibility.EVERYONE ||
      privacySettings?.seePicture === EPrivacyVisibility.CONTACTS
    );
  }, [privacySettings]);

  const seeContact = useMemo(() => {
    return (
      privacySettings?.seeContact === EPrivacyVisibility.EVERYONE ||
      privacySettings?.seeContact === EPrivacyVisibility.CONTACTS
    );
  }, [privacySettings]);

  const seeIntro = useMemo(() => {
    return (
      privacySettings?.seeIntro === EPrivacyVisibility.EVERYONE ||
      privacySettings?.seeIntro === EPrivacyVisibility.CONTACTS
    );
  }, [privacySettings]);

  const isPrivate = useMemo(() => {
    return !seeContact && !seeIntro && !seeImage;
  }, [seeIntro, seeContact, seeImage]);

  const subTextContent = useMemo(() => {
    if (isPrivate) {
      return t("modals:network_profile.private");
    }

    return (
      getCountryDataByCode({ code: userInfo?.country })?.name ||
      t("modals:network_profile.no_country")
    );
  }, [getCountryDataByCode, userInfo]);

  return (
    <>
      <ContactCard
        type={EContactCardType.SEND}
        actions={[
          {
            tooltip: t("common:buttons.invite"),
            icon: <MdPersonAddAlt1 size={ICON_SIZES.MD} />,
            onClick: (e: any) => {
              e?.preventDefault?.();
              setShowSendDialog(true);
            },
            kind: EButtonKind.SAVE,
          },
        ]}
        index={animationIndex}
        subText={subTextContent}
        name={userInfo?.name}
        picture={userImage}
        onClick={() => setShowUserInfoModal(true)}
      />

      {showUserInfoModal && (
        <NetworkProfile
          profileInfo={userInfo}
          onClose={() => setShowUserInfoModal(false)}
          actions={[
            {
              onClick: () => setShowSendDialog(true),
              icon: <MdPersonAddAlt1 size={ICON_SIZES.MD} />,
              kind: EButtonKind.SAVE,
              text: t("common:buttons.invite"),
              isLoading: isInviting,
            },
          ]}
        />
      )}

      {showSendDialog && (
        <ConfirmDialog
          description={`¿Deseas enviar una solicitud de contacto a ${getShortName(
            userInfo?.name,
          )}?`}
          onConfirm={async () => {
            setIsInviting(true);
            const res = await sendContactInvite({
              sentByName: currentUserInfo?.name,
              sentToEmail: userInfo?.email,
              sentToName: userInfo?.name,
              sentToUserId: userInfo?._id,
            });

            if (res) {
              showNotification({
                kind: "success",
                message: "Invitación enviada",
              });
              await resetInvitesCache();
              setIsInviting(false);
              setShowSendDialog(false);
              setShowUserInfoModal(false);
            } else {
              setIsInviting(false);
              showNotification({
                kind: "error",
                message: "Error enviando la invitación",
              });
            }
          }}
          onClose={() => setShowSendDialog(false)}
          confirmKind={EButtonKind.SAVE}
          title="Invitar"
          isLoading={isInviting}
          doubleConfirmRequired
        />
      )}
    </>
  );
};

export default memo(SendContactInviteCard);
